import { useState, useEffect } from 'react'
import ProofreadingDashboard from './components/ProofreadingDashboard'
import DebugViewer from './components/DebugViewer'
import URLCheckPage from './components/URLCheckPage'
import VersionPage from './components/VersionPage'
import GlobalRevisionsSection from './components/globalRevisions/GlobalRevisionsSection'
import NavigationBar from './components/app/NavigationBar'
import FeaturedHero from './components/app/FeaturedHero'
import StoreGrid from './components/app/StoreGrid'
import { useAuth } from './contexts/AuthContext'
import { authenticatedFetch } from './utils/authenticatedFetch'
import validationQueue from './utils/validationQueue'
import { getStoreStatus, getStatusCounts } from './utils/statusUtils'
import { transformCompanyData, loadStoresWithValidation, createValidationFunction } from './utils/storeUtils'
import './App.css'

function App() {
  const { user, logout } = useAuth();
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [query, setQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('stores');
  const [queueStatus, setQueueStatus] = useState({
    queueLength: 0,
    isProcessing: false,
    currentValidation: null,
    queuedStores: []
  });
  const [currentlyValidatingStore, setCurrentlyValidatingStore] = useState(null);
  const [validationProgressByStore, setValidationProgressByStore] = useState({});

  // Load stores whenever page changes
  useEffect(() => {
    const loadStores = async () => {
      try {
        const storesData = await loadStoresWithValidation(authenticatedFetch, transformCompanyData);
        setStores(storesData);
        setFilteredStores(storesData);
      } catch (error) {
        console.error('Failed to load stores:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStores();
  }, []);

  // Listen to validation queue status
  useEffect(() => {
    const unsubscribe = validationQueue.addListener((status) => {
      setQueueStatus(status);
      setCurrentlyValidatingStore(status.currentValidation?.storeInfo?.name || null);
    });

    setQueueStatus(validationQueue.getStatus());

    return unsubscribe;
  }, []);

  // Filter stores based on search query and status filter
  useEffect(() => {
    let filtered = stores;

    if (query.trim() !== '') {
      filtered = filtered.filter(store =>
        store.name.toLowerCase().includes(query.toLowerCase()) ||
        store.region.toLowerCase().includes(query.toLowerCase()) ||
        store.address?.toLowerCase().includes(query.toLowerCase()) ||
        store.pages.some(page =>
          page.domain.toLowerCase().includes(query.toLowerCase()) ||
          page.type.toLowerCase().includes(query.toLowerCase())
        )
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(store => {
        const status = getStoreStatus(store);
        return status === filterStatus;
      });
    }

    setFilteredStores(filtered);
  }, [query, filterStatus, stores]);

  const statusCounts = getStatusCounts(stores);

  // Handler for when a store completes validation
  const handleValidationComplete = (storeName, validationResults) => {
    setStores(prevStores =>
      prevStores.map(store =>
        store.name === storeName
          ? { ...store, validationResults }
          : store
      )
    );
  };

  // Handler to check all filtered stores
  const handleCheckAllPages = () => {
    const storesToValidate = filteredStores.filter(store => store.pages.length > 0);

    if (storesToValidate.length === 0) {
      return;
    }

    storesToValidate.forEach(store => {
      const performValidation = createValidationFunction(
        store,
        setValidationProgressByStore,
        handleValidationComplete
      );

      validationQueue.addToQueue(
        {
          name: store.name,
          validationFunction: performValidation
        },
        null,
        null,
        (error) => {
          console.error('Queue validation error for', store.name, error);
        }
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="text-center animate-fade-in">
          <div className="spinner mx-auto"></div>
          <p className="mt-6 text-lg font-medium" style={{ color: 'var(--color-text-secondary)' }}>
            Loading stores...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <NavigationBar
        currentView={currentView}
        setCurrentView={setCurrentView}
        user={user}
        logout={logout}
        queueStatus={queueStatus}
        currentlyValidatingStore={currentlyValidatingStore}
      />

      <div className="app-container py-12">
        {currentView === 'dashboard' ? (
          <ProofreadingDashboard stores={stores} />
        ) : currentView === 'urlcheck' ? (
          <URLCheckPage stores={stores} />
        ) : currentView === 'debug' ? (
          <DebugViewer />
        ) : currentView === 'version' ? (
          <VersionPage />
        ) : (
          <>
            <FeaturedHero
              query={query}
              setQuery={setQuery}
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              statusCounts={statusCounts}
            />

            <GlobalRevisionsSection />

            <StoreGrid
              filteredStores={filteredStores}
              stores={stores}
              query={query}
              filterStatus={filterStatus}
              queueStatus={queueStatus}
              handleCheckAllPages={handleCheckAllPages}
              handleValidationComplete={handleValidationComplete}
              currentlyValidatingStore={currentlyValidatingStore}
              validationProgressByStore={validationProgressByStore}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default App
